/**
 * DictionaryPopover Component
 * Displays dictionary information in a popover
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { dictionaryService, DictionaryWord } from '../services/dictionaryService';
import { cefrKnowledgeService, CEFRLevel } from '../services/cefrKnowledgeService';
import { getLemmas } from '../utils/lemmatizer';
import { formatPOS } from '../utils/posLabels';
import { CEFRGuideModal } from './cefr';
import './DictionaryPopover.css';

import { API_BASE_URL } from '../config';

/**
 * Transform audio URL to use the correct API base URL
 * Handles cases where backend returns localhost URLs but client is on different device
 */
const getProperAudioUrl = (url: string): string => {
  if (!url) return url;

  let finalUrl = url;

  // Replace localhost URLs with the actual API base URL
  if (/\/(localhost|127\.0\.0\.1):\d+/.test(url)) {
    const path = url.replace(/^https?:\/\/(localhost|127\.0\.0\.1):\d+/, '');
    finalUrl = `${API_BASE_URL}${path}`;
  } else if (url.startsWith('/')) {
    // If it's a relative URL, prepend API base URL
    finalUrl = `${API_BASE_URL}${url}`;
  }

  // Encode spaces in URL path (e.g., "walks into.mp3" → "walks%20into.mp3")
  return finalUrl.replace(/ /g, '%20');
};

interface DictionaryPopoverProps {
  word: string;
  position: { x: number; y: number };
  onClose: () => void;
  courseId?: string;
  onAddNote?: (word: string, content: string) => void;
}

const MOBILE_BREAKPOINT = 640;

export const DictionaryPopover: React.FC<DictionaryPopoverProps> = ({ word, position, onClose, courseId, onAddNote }) => {
  const { t } = useTranslation('common');
  const [data, setData] = useState<DictionaryWord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [addingToVocab, setAddingToVocab] = useState(false);
  const [vocabAdded, setVocabAdded] = useState(false);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [noteAdded, setNoteAdded] = useState(false);
  const [cefrLevel, setCefrLevel] = useState<CEFRLevel | null>(null);
  const [showCefrGuide, setShowCefrGuide] = useState(false);
  const [isMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [viewportTop, setViewportTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const popoverRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Anchor the top-sheet to the visible viewport so the keyboard never covers it.
  useEffect(() => {
    if (!isMobile) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      setViewportTop(vv.offsetTop);
      setViewportHeight(vv.height);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [isMobile]);

  // Fetch word data
  useEffect(() => {
    const fetchWord = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await dictionaryService.lookup(word, { generateAudio: true });
        setData(result);
      } catch (err: any) {
        setError(err.message || t('failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchWord();
  }, [word, t]);

  // Reflect existing vocab-book membership so reopening the popover for a
  // saved word shows "已收錄" instead of "加入".
  useEffect(() => {
    let cancelled = false;
    setVocabAdded(false);

    const checkVocabMembership = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token || !word) return;
      try {
        const resp = await fetch(
          `${API_BASE_URL}/api/v1/vocabulary-book/check?word=${encodeURIComponent(word)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!resp.ok) return;
        const json = await resp.json();
        if (!cancelled && json?.data?.inBook) {
          setVocabAdded(true);
        }
      } catch {
        // Non-fatal — keep default "加入" state
      }
    };

    checkVocabMembership();
    return () => { cancelled = true; };
  }, [word]);

  // Fetch CEFR level
  useEffect(() => {
    let isMounted = true;
    setCefrLevel(null);

    const fetchCefrLevel = async () => {
      try {
        const lemmas = getLemmas(word);
        // Search for each lemma form until we find a CEFR match
        for (const lemma of lemmas) {
          if (!isMounted) return;
          const results = await cefrKnowledgeService.searchKnowledgePoints(lemma, 3);
          const match = results.find(r => r.term.toLowerCase() === lemma);
          if (match) {
            if (isMounted) setCefrLevel(match.cefrLevel);
            return;
          }
        }
      } catch (err) {
        // Silently fail - CEFR level is optional
      }
    };

    fetchCefrLevel();
    return () => { isMounted = false; };
  }, [word]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Play audio
  const playAudio = async (audioUrl: string, index: number) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    const properUrl = getProperAudioUrl(audioUrl);
    const audio = new Audio(properUrl);
    audioRef.current = audio;
    setPlayingIndex(index);

    audio.onended = () => {
      setPlayingIndex(null);
    };

    try {
      await audio.play();
    } catch {
      // Interrupted by a new play/pause — safe to ignore
    }
  };

  // Add to vocabulary book
  const handleAddToVocab = async () => {
    if (!data || addingToVocab || vocabAdded) return;

    setAddingToVocab(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        alert(t('dictionary.pleaseLogin'));
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/vocabulary-book/word`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          word: data.word,
          phonetic: data.phonetic || '',
          translation: data.translation || '',
        }),
      });

      if (response.ok) {
        setVocabAdded(true);
      } else {
        const errData = await response.json();
        alert(errData.message || t('dictionary.addToVocabFailed'));
      }
    } catch (err) {
      console.error('Failed to add to vocabulary:', err);
      alert(t('dictionary.addToVocabFailed'));
    } finally {
      setAddingToVocab(false);
    }
  };

  // Add note
  const handleAddNote = async () => {
    if (!noteContent.trim() || addingNote) return;

    if (onAddNote) {
      onAddNote(word, noteContent);
      setNoteAdded(true);
      setShowNoteInput(false);
      return;
    }

    if (!courseId) {
      alert(t('dictionary.useInCoursePage'));
      return;
    }

    setAddingNote(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        alert(t('dictionary.pleaseLogin'));
        return;
      }

      const response = await fetch(`${API_BASE_URL}/v1/learning/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          courseId,
          content: noteContent,
          relatedWord: word,
          tags: ['dictionary'],
        }),
      });

      if (response.ok) {
        setNoteAdded(true);
        setShowNoteInput(false);
      } else {
        const errData = await response.json();
        alert(errData.message || t('dictionary.addNoteFailed'));
      }
    } catch (err) {
      console.error('Failed to add note:', err);
      alert(t('dictionary.addNoteFailed'));
    } finally {
      setAddingNote(false);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Use React Portal to render popover at document body level
  // This avoids DOM nesting errors when popover is inside <p> tags
  return ReactDOM.createPortal(
    <>
      {isMobile && <div className="dictionary-popover-backdrop" onClick={onClose} />}
      <div
        ref={popoverRef}
        className="dictionary-popover"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={isMobile ? ({
          '--dict-top': `${viewportTop}px`,
          '--dict-max-h': viewportHeight > 0
            ? `${Math.max(viewportHeight - 24, 240)}px`
            : '85vh',
        } as React.CSSProperties) : {
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      >
      {loading && (
        <div className="popover-loading">
          <div className="spinner-small"></div>
          <span>{t('loading')}</span>
        </div>
      )}

      {error && (
        <div className="popover-error">
          <span>{error}</span>
        </div>
      )}

      {data && !loading && (
        <div className="popover-content">
          {/* Header: Word + Phonetics */}
          <div className="popover-header">
            <h3 className="word">{data.word}</h3>

            {/* Compound-word subtitle — shown when the backend split "gene-edited" into parts */}
            {data.compound && (
              <div className="compound-subtitle">{t('dictionary.compoundWord', { defaultValue: '複合字 · compound word' })}</div>
            )}

            {/* Phonetics with Audio - inline with word or below */}
            {!data.compound && data.audio && data.audio.length > 0 && data.audio.some(a => a.phonetic || a.url) && (
              <div className="phonetics-inline">
                {data.audio
                  .filter(audio => audio.phonetic || audio.url)
                  .map((audio, index) => (
                  <div key={index} className="phonetic-item">
                    {(data.audio?.filter(a => a.phonetic || a.url).length ?? 0) > 1 && (
                      <span className="phonetic-label">{index === 0 ? t('dictionary.americanPhonetic') : t('dictionary.britishPhonetic')}</span>
                    )}
                    {audio.phonetic && (
                      <span className="phonetic">/{audio.phonetic}/</span>
                    )}
                    {audio.url && (
                      <button
                        className={`audio-btn ${playingIndex === index ? 'playing' : ''}`}
                        onClick={() => playAudio(audio.url, index)}
                        title={t('dictionary.playPronunciation')}
                      >
                        {playingIndex === index ? (
                          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Single phonetic (no audio) - only show if phonetic exists */}
            {!data.compound && data.phonetic && (!data.audio || data.audio.length === 0 || !data.audio.some(a => a.phonetic || a.url)) && (
              <div className="phonetics-inline">
                <span className="phonetic">/{data.phonetic}/</span>
              </div>
            )}
          </div>

          {/* Tags: CEFR Level (suppressed for compound — each part shows its own) */}
          {!data.compound && cefrLevel && (
            <div className="popover-tags">
              <span
                className={`popover-tag popover-tag--cefr popover-tag--cefr-${cefrLevel} popover-tag--clickable`}
                onClick={() => setShowCefrGuide(true)}
              >
                CEFR {cefrLevel}
              </span>
            </div>
          )}

          {/* Compound parts — stacked mini-cards, one per hyphenated sub-word */}
          {data.compound && (
            <div className="compound-parts">
              {data.compound.parts.map((part, partIdx) => {
                // Use a high offset so audio indexes don't collide with the primary word's variants
                const audioIdxBase = 1000 + partIdx * 10;
                const audioVariants = (part.audio || []).filter(a => a.phonetic || a.url);
                return (
                  <div key={`compound-${partIdx}-${part.word}`} className={`compound-part ${part.found ? '' : 'compound-part--missing'}`}>
                    <div className="compound-part-header">
                      <span className="compound-part-word">{part.word}</span>
                      {part.phonetic && (
                        <span className="compound-part-phonetic">/{part.phonetic}/</span>
                      )}
                      {audioVariants.map((audio, audioIdx) => {
                        const idx = audioIdxBase + audioIdx;
                        return audio.url ? (
                          <button
                            key={idx}
                            className={`audio-btn ${playingIndex === idx ? 'playing' : ''}`}
                            onClick={() => playAudio(audio.url, idx)}
                            title={t('dictionary.playPronunciation')}
                          >
                            {playingIndex === idx ? (
                              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                <path d="M8 5v14l11-7z"/>
                              </svg>
                            )}
                          </button>
                        ) : null;
                      })}
                      {part.cefrLevel && (
                        <span className={`popover-tag popover-tag--cefr popover-tag--cefr-${part.cefrLevel}`}>
                          {part.cefrLevel}
                        </span>
                      )}
                    </div>
                    {part.found && part.translation ? (
                      <div className="compound-part-translation">
                        {part.translation.split('\n').map((line, lineIdx) => {
                          const cleanLine = line.replace(/^\w[\w-]*:\s*/, '');
                          const posMatch = cleanLine.match(/^([a-z]+\.)\s+(.+)$/i);
                          if (posMatch) {
                            return (
                              <p key={lineIdx}>
                                <span className="pos-label">{formatPOS(posMatch[1])}</span>
                                {posMatch[2]}
                              </p>
                            );
                          }
                          return cleanLine.trim() ? <p key={lineIdx}>{cleanLine}</p> : null;
                        })}
                      </div>
                    ) : (
                      <div className="compound-part-missing">
                        {t('dictionary.notInDictionary', { defaultValue: '未收錄於字典' })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Heteronym Alert - Show warning for words with multiple pronunciations */}
          {!data.compound && data.heteronym && data.heteronym.variants.length > 1 && (
            <div className="heteronym-alert">
              <div className="heteronym-header">
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" style={{ marginRight: '6px', flexShrink: 0 }}>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                </svg>
                <span style={{ fontWeight: 'bold', color: '#B45309' }}>{t('dictionary.heteronym')}</span>
              </div>
              <div className="heteronym-variants">
                {data.heteronym.variants.map((variant, idx) => (
                  <div key={idx} className="heteronym-variant">
                    <div className="variant-header">
                      <span className="variant-phonetic">/{variant.phonetic}/</span>
                      <span className="variant-pos">{variant.partOfSpeech}</span>
                      {variant.tense && (
                        <span className="variant-tense">({variant.tense === 'present' ? t('dictionary.presentTense') : variant.tense === 'past' ? t('dictionary.pastTense') : variant.tense})</span>
                      )}
                    </div>
                    <div className="variant-meaning">
                      <span className="meaning-zh">{variant.meaning_native}</span>
                    </div>
                    {variant.context && (
                      <div className="variant-context">
                        <span className="context-example">{variant.context}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {data.heteronym.note_native && (
                <div className="heteronym-note">
                  {data.heteronym.note_native}
                </div>
              )}
            </div>
          )}

          {/* Translation (suppressed for compounds — parts rendered above) */}
          {!data.compound && data.translation && (
            <div className="translation">
              {data.translation.split('\n').map((line, index) => {
                // Strip "word: " prefix (Gemini translation artifact)
                const cleanLine = line.replace(/^\w[\w-]*:\s*/, '');
                // Check if line starts with part of speech (e.g., "n. ", "v. ", "a. ")
                const posMatch = cleanLine.match(/^([a-z]+\.)\s+(.+)$/i);
                if (posMatch) {
                  return (
                    <p key={index}>
                      <span className="pos-label">{formatPOS(posMatch[1])}</span>
                      {posMatch[2]}
                    </p>
                  );
                }
                // Line without POS marker
                return cleanLine.trim() ? <p key={index}>{cleanLine}</p> : null;
              })}

              {/* Display base word info for inflected forms */}
              {data.wordForms?.lemma && (
                <div className="lemma-section">
                  <div className="lemma-header">
                    <span className="lemma-arrow">{data.word} →{' '}</span>
                    <span
                      className="lemma-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        setData(null);
                        setLoading(true);
                        const lemma = data.wordForms!.lemma!;
                        dictionaryService.lookup(lemma, { generateAudio: true }).then((result) => {
                          setData(result);
                          setLoading(false);
                        }).catch(() => setLoading(false));
                      }}
                    >
                      {data.wordForms.lemma}
                    </span>
                    {data.wordForms.formType && (
                      <span className="form-type-badge">
                        {t(`dictionary.formType.${data.wordForms.formType}`, { defaultValue: data.wordForms.formType })}
                      </span>
                    )}
                  </div>
                  {data.lemmaTranslation && (
                    <div className="lemma-translation">
                      {data.lemmaTranslation.split('\n').map((line, index) => {
                        const cleanLine = line.replace(/^\w[\w-]*:\s*/, '');
                        const posMatch = cleanLine.match(/^([a-z]+\.)\s+(.+)$/i);
                        if (posMatch) {
                          return (
                            <p key={`lemma-${index}`}>
                              <span className="pos-label">{formatPOS(posMatch[1])}</span>
                              {posMatch[2]}
                            </p>
                          );
                        }
                        return cleanLine.trim() ? <p key={`lemma-${index}`}>{cleanLine}</p> : null;
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="popover-actions">
            <button
              className={`action-btn vocab-btn ${vocabAdded ? 'added' : ''}`}
              onClick={handleAddToVocab}
              disabled={addingToVocab || vocabAdded}
            >
              {addingToVocab ? (
                <span className="btn-loading">{t('dictionary.adding')}</span>
              ) : vocabAdded ? (
                <>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  {t('dictionary.addedToVocab')}
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                  </svg>
                  {t('dictionary.addToVocab')}
                </>
              )}
            </button>

            {(courseId || onAddNote) && (
              <button
                className={`action-btn note-btn ${noteAdded ? 'added' : ''}`}
                onClick={() => !noteAdded && setShowNoteInput(!showNoteInput)}
                disabled={noteAdded}
              >
                {noteAdded ? (
                  <>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    {t('dictionary.noteAdded')}
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 7h10v2H7zm0 4h10v2H7zm0 4h7v2H7z"/>
                    </svg>
                    {t('dictionary.addNote')}
                  </>
                )}
              </button>
            )}
          </div>

          {/* Note input area */}
          {showNoteInput && (
            <div className="note-input-area">
              <textarea
                className="note-textarea"
                placeholder={t('dictionary.notePlaceholder')}
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                rows={2}
              />
              <div className="note-input-actions">
                <button
                  className="note-cancel-btn"
                  onClick={() => {
                    setShowNoteInput(false);
                    setNoteContent('');
                  }}
                >
                  {t('cancel')}
                </button>
                <button
                  className="note-submit-btn"
                  onClick={handleAddNote}
                  disabled={!noteContent.trim() || addingNote}
                >
                  {addingNote ? t('dictionary.savingNote') : t('dictionary.saveNote')}
                </button>
              </div>
            </div>
          )}

          {/* Close button */}
          <button className="close-btn" onClick={onClose} title={t('close')}>
            ✕
          </button>
        </div>
      )}
    </div>
    <CEFRGuideModal isOpen={showCefrGuide} onClose={() => setShowCefrGuide(false)} />
    </>,
    document.body
  );
};
